// Ingestor health banner (v0.8.9).
//
// Shown to admins at the top of every page while any polling ingestor
// is failing. Deliberately NOT dismissible-persistently: the dismiss
// button only hides it for the current page session, so it returns on
// the next sign-in. The operator's requirement was "I need to know an
// ingestor is failing" — a banner you can silence forever is one you
// will silence forever.
//
// The failure it was built for: six GitHub ingestors sat dead for six
// weeks, polling every ten minutes, each reporting success.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface QuietIngestor {
  id: string;
  type: string;
  name: string;
  team_id: string;
  team_name: string | null;
  last_event_at: string | null;
  ever_produced: boolean;
  hours_silent: number | null;
  last_poll_at: string | null;
  typical_gap_hours: number | null;
  threshold_hours: number;
}

interface FailingIngestor {
  id: string;
  type: string;
  name: string;
  team_id: string;
  team_name: string | null;
  last_error: string;
  last_error_at: string | null;
  last_poll_at: string | null;
}

/** Compact "3 days ago" / "2 hours ago" for the banner. */
export function failingFor(iso: string | null, now = Date.now()): string {
  if (!iso) return 'an unknown time';
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.floor(mins / 60);
  // Switch to days at 24h, not 48h — "24 hours" for something that
  // broke yesterday reads worse than "1 day".
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function IngestorHealthBanner() {
  const [dismissed, setDismissed] = useState(false);
  const health = useQuery({
    queryKey: ['ingestor-health'],
    queryFn: () => api<{
      failing: FailingIngestor[];
      count: number;
      quiet: QuietIngestor[];
      quiet_count: number;
      quiet_hours: number;
    }>('/api/admin/ingestor-health'),
    // Polling ingestors run on a 10-minute cadence, so anything
    // faster than a few minutes is wasted. A failure also self-clears
    // on the next good poll, so the banner should go away on its own.
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  const failing = health.data?.failing ?? [];
  const quiet = health.data?.quiet ?? [];
  if (dismissed || (failing.length === 0 && quiet.length === 0)) return null;

  // Errors get the alarm; silence gets a warning. They are different
  // problems — a poller can be perfectly healthy and simply have had
  // nothing to fetch — so they must not look the same.
  if (failing.length === 0) {
  return (
    <div
      role="status"
      className="border-b border-amber-500/40 bg-amber-950/50 px-4 py-2.5 text-sm text-amber-100"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3">
        <span aria-hidden className="mt-0.5 text-base">⏳</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {quiet.length === 1
              ? 'Ingestor has gone quiet'
              : `${quiet.length} ingestors have gone quiet`}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {quiet.map((q) => (
              <li key={q.id} className="text-xs text-amber-200/90">
                <span className="font-mono">{q.type}</span> · {q.name}
                {' — '}
                {q.ever_produced ? (
                  <>
                    no events in {Math.round((q.hours_silent ?? 0) / 24)} day
                    {Math.round((q.hours_silent ?? 0) / 24) === 1 ? '' : 's'}
                    {q.typical_gap_hours !== null && q.typical_gap_hours > 0 ? (
                      <span className="text-amber-200/60">
                        {' '}(normally every {q.typical_gap_hours < 1
                          ? '<1'
                          : Math.round(q.typical_gap_hours)}h)
                      </span>
                    ) : null}
                  </>
                ) : (
                  'has never produced any events'
                )}
              </li>
            ))}
          </ul>
          <p className="mt-0.5 text-[11px] text-amber-200/70">
            Polling still succeeds, so this may be a permission problem rather than an
            outage — but nothing has arrived well past each one's normal rhythm.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Link to="/admin/ingestors" className="underline hover:no-underline">Check</Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-amber-200/70 hover:text-amber-100"
            aria-label="Dismiss for this session"
            title="Hides until you sign in again"
          >✕</button>
        </div>
      </div>
    </div>
  );
  }

  const first = failing[0]!;
  const many = failing.length > 1;

  return (
    <div
      role="alert"
      className="border-b border-red-500/40 bg-red-950/60 px-4 py-2.5 text-sm text-red-100"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3">
        <span aria-hidden className="mt-0.5 text-base">⚠</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {many
              ? `${failing.length} ingestors are failing`
              : `Ingestor failing: ${first.name}`}
          </p>
          <p className="mt-0.5 text-red-200/90">
            {many ? (
              <>Last error: <span className="font-mono text-xs">{first.last_error}</span></>
            ) : (
              <>
                {first.team_name ? `${first.team_name} · ` : ''}
                <span className="font-mono text-xs">{first.last_error}</span>
              </>
            )}
            {' · '}failing for {failingFor(first.last_error_at)}
          </p>
          {many ? (
            <ul className="mt-1 space-y-0.5">
              {failing.map((f) => (
                <li key={f.id} className="text-xs text-red-200/80">
                  <span className="font-mono">{f.type}</span> · {f.name}
                  {f.team_name ? ` · ${f.team_name}` : ''}
                  {' — '}
                  <span className="font-mono">{f.last_error}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Link to="/admin/ingestors" className="underline hover:no-underline">
            Fix
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-red-200/70 hover:text-red-100"
            aria-label="Dismiss for this session"
            title="Hides until you sign in again"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
